import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { EditorView } from '@atlaskit/editor-prosemirror/view';

import {
  focusOnCodeSuggestionsEditor,
  getAllNodePositions,
  isCodeSuggestionNode,
} from './utils';

/**
 * Creates a new paragraph node at the given position.
 *
 * This is used when navigating "past" the last block (e.g., pressing ArrowDown from the bottom-most block).
 * Since there's no next node, we create a placeholder paragraph to move the cursor into.
 */
export const createParagraphNodeAtPos = (
  view: EditorView,
  position: number
) => {
  const { state, dispatch } = view;
  const { tr } = state;

  const paragraph = state.schema.nodes.paragraph.create();
  tr.insert(position, paragraph);

  // Place the selection near the newly inserted paragraph
  const resolvedPosition = tr.doc.resolve(position);
  const selection = TextSelection.near(resolvedPosition);
  dispatch(tr.setSelection(selection).scrollIntoView());
};

/**
 * Selects the extension node at a given position.
 *
 * This is used to select the over all node when the user presses ArrowRight or ArrowLeft
 */
export const selectNodeAtPos = (outerEditorView: EditorView, pos: number) => {
  const { state, dispatch } = outerEditorView;
  const resolvedPos = state.doc.resolve(pos);
  const selection = TextSelection.near(resolvedPos);

  dispatch(state.tr.setSelection(selection).scrollIntoView());
  outerEditorView.focus();
};

/**
 * Deletes the extension node at the specified index,
 * then moves the cursor to a nearby node to keep focus in the editor.
 */
export const deleteExtensionNode = (
  editorView: EditorView,
  currentExtensionNodeIndex: number,
  nodePositions: number[]
) => {
  const { state, dispatch } = editorView;
  const { tr } = state;
  const position = nodePositions[currentExtensionNodeIndex];
  const node = state.doc.nodeAt(position);

  if (!node) return;

  if (currentExtensionNodeIndex !== 0) {
    // If not the first node, delete and move cursor to the *end* of the previous node
    tr.delete(position, position + node.nodeSize);
    // set the position to the end of the previous node
    const prevNodePos = nodePositions[currentExtensionNodeIndex - 1];
    const prevNode = state.doc.nodeAt(prevNodePos);
    if (!prevNode) return;

    const prevNodePosResolved = tr.doc.resolve(
      prevNodePos + prevNode.nodeSize - 1
    );
    const selection = TextSelection.near(prevNodePosResolved);
    dispatch(tr.setSelection(selection).scrollIntoView());
  } else {
    // If it is the first node, reset cursor to top of document
    const startPos = tr.doc.resolve(0);
    const selection = TextSelection.near(startPos);
    dispatch(tr.setSelection(selection).scrollIntoView());
  }
  editorView.focus();
};

/**
 * Handles vertical arrow key navigation *between* blocks.
 * Supports jumping into the next code suggestion or out to regular ProseMirror content.
 *
 * If the user is at the start or end of a CodeMirror block and presses up/down,
 * this determines the next appropriate destination.
 */
const handleArrow = ({
  currentExtensionNodeIndex,
  nodePositions,
  pmEditorView,
  direction,
}: {
  currentExtensionNodeIndex: number;
  nodePositions: number[];
  pmEditorView: EditorView | null;
  direction: 'up' | 'down';
}) => {
  if (!pmEditorView) return;

  const { state, dispatch } = pmEditorView;
  const { tr } = state;

  // Determine next node position based on direction
  const nextNodeIdx =
    direction === 'down'
      ? currentExtensionNodeIndex + 1
      : currentExtensionNodeIndex - 1;

  const currentNodePos = nodePositions[currentExtensionNodeIndex];
  const currentNode = state.doc.nodeAt(
    nodePositions[currentExtensionNodeIndex]
  );
  let insertPos = currentNodePos;

  // If there is no next block (e.g., bottom of the doc), insert a paragraph to move into
  if (nextNodeIdx < 0 || nextNodeIdx >= nodePositions.length) {
    if (direction === 'down') {
      insertPos = currentNode
        ? currentNodePos + currentNode.nodeSize
        : currentNodePos;
    }

    createParagraphNodeAtPos(pmEditorView, insertPos);
    pmEditorView.focus();
    return;
  }

  const nextNodePos = nodePositions[nextNodeIdx];
  const nextNode = state.doc.nodeAt(nextNodePos);

  if (nextNode) {
    if (nextNode && isCodeSuggestionNode(nextNode)) {
      const { localId } = nextNode.attrs;
      // If the next node is a code suggestion, focus into its CodeMirror editor
      focusOnCodeSuggestionsEditor(localId);
    } else {
      // Otherwise, move ProseMirror's selection into that block
      const nextNodePosResolved = tr.doc.resolve(
        nextNodePos + (direction === 'up' ? nextNode.nodeSize - 1 : 0)
      );
      const selection = TextSelection.near(nextNodePosResolved);
      dispatch(state.tr.setSelection(selection).scrollIntoView());
      pmEditorView.focus();
    }
  }
};

/**
 * Central handler for CodeMirror keydown events.
 * Allows for:
 * - vertical navigation
 * - deletion of the code suggestion extension block
 * - horizontal exit into surrounding content
 */
export const handleEditorKeydown = ({
  event,
  cmEditor,
  pmEditorView,
  nodeLocalId,
}: {
  event: KeyboardEvent;
  cmEditor: any;
  pmEditorView: EditorView | null;
  nodeLocalId: string;
}) => {
  if (!pmEditorView) return;

  const { state } = pmEditorView;
  const { nodePositions, currentExtensionNodeIndex } = getAllNodePositions(
    state,
    nodeLocalId
  );

  // cursor and line position helps determine when we're eligible
  // to leave the CodeMirror editor
  const cursor = cmEditor.getCursor();
  const lineContent = cmEditor.getLine(cursor.line);
  const lineCount = cmEditor.lineCount();

  switch (event.key) {
    case 'ArrowDown':
      // When pressing down on the last line, move focus to the next block
      if (cursor.line === lineCount - 1) {
        handleArrow({
          currentExtensionNodeIndex,
          nodePositions,
          pmEditorView,
          direction: 'down',
        });
      }
      break;

    case 'ArrowUp':
      // When pressing up on the first line, move to previous block
      if (cursor.line === 0) {
        handleArrow({
          currentExtensionNodeIndex,
          nodePositions,
          pmEditorView,
          direction: 'up',
        });
      }
      break;

    case 'ArrowRight':
    case 'ArrowLeft':
      // When pressing right on the last character of the last line, or
      // when pressing left on the first character of the first line,
      // highlight the extension node
      if (cursor.line === lineCount - 1 && cursor.ch === lineContent.length) {
        selectNodeAtPos(pmEditorView, nodePositions[currentExtensionNodeIndex]);
      }
      break;

    case 'Backspace':
      // If backspacing at the very start of the block, delete the current extension node
      if (cursor.line === 0 && cursor.ch === 0) {
        deleteExtensionNode(
          pmEditorView,
          currentExtensionNodeIndex,
          nodePositions
        );
      }
      break;

    default:
      break;
  }
};
